import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Building2, MapPin, Users, CheckCircle2 } from 'lucide-react';
import { PTPDocument } from './RiskAreasWidget';
import { useEscapeKey } from '../hooks/useEscapeKey';
interface PTPDocumentViewerProps {
  document: PTPDocument | null;
  onClose: () => void;
}
export function PTPDocumentViewer({
  document,
  onClose
}: PTPDocumentViewerProps) {
  useEscapeKey(onClose, !!document);
  if (!document) return null;
  const engagementRate = Math.round(document.signatureCount / document.totalWorkers * 100);
  const isLowEngagement = engagementRate < 70;
  return <AnimatePresence data-id="element-3022" data-mp="39ex4fb1oredn">
      {document && <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-3023" data-mp="3efchs2lywlvm">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-3024" data-mp="5z9juoknqbkh" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        duration: 0.5,
        bounce: 0.3
      }} className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" data-id="element-3025" data-mp="8qzkotkpk3gl">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-3026" data-mp="379odv59gjdf9">
              <div data-id="element-3027" data-mp="339w8t2j9vpen">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-3028" data-mp="33anvfnuw6css">
                  Pre-Task Plan
                </h3>
                <p className="text-sm text-gray-500" data-id="element-3029" data-mp="16al2qfbys3b5">
                  Safety documentation review
                </p>
              </div>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-3030" data-mp="1g64wow6ywrx9">
                <X size={20} data-id="element-3031" data-mp="1p7cfe5x03k68" />
              </button>
            </div>

            {/* Document Info */}
            <div className="px-6 py-4 bg-blue-50 border-b border-blue-100" data-id="element-3032" data-mp="1u5nqsl2y22qk">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3033" data-mp="1yww25mnu3y6d">
                <div className="flex items-center gap-2" data-id="element-3034" data-mp="1yxnxcwdirtw4">
                  <Calendar size={16} className="text-blue-600" data-id="element-3035" data-mp="23w8n21v5av6b" />
                  <div data-id="element-3036" data-mp="28v22hztzsw94">
                    <p className="text-xs text-blue-600 font-medium" data-id="element-3037" data-mp="28vlj9d7wpelk">Date</p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-3038" data-mp="2cwnc9cb8cuf3">
                      {document.date}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-3039" data-mp="177bnnd0fpa8">
                  <Building2 size={16} className="text-blue-600" data-id="element-3040" data-mp="3tc1h3qe7xain" />
                  <div data-id="element-3041" data-mp="3odrlxfshu9x1">
                    <p className="text-xs text-blue-600 font-medium" data-id="element-3042" data-mp="3od8562ekxrkl">Company</p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-3043" data-mp="3jf6n9ksw3yap">
                      {document.company}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-3044" data-mp="fy7w6qr179mq">
                  <MapPin size={16} className="text-blue-600" data-id="element-3045" data-mp="fyq1ecvo8y6s" />
                  <div data-id="element-3046" data-mp="b6y6jq5hg6au">
                    <p className="text-xs text-blue-600 font-medium" data-id="element-3047" data-mp="b66oli4t6731">
                      Location
                    </p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-3048" data-mp="684ve4do4rdu">
                      {document.location}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-3049" data-mp="yvhgsb83et5k">
                  <Users size={16} className={isLowEngagement ? 'text-amber-600' : 'text-emerald-600'} data-id="element-3050" data-mp="jej6tgt2ee2s" />
                  <div data-id="element-3051" data-mp="9hel1i34skmi">
                    <p className="text-xs font-medium" style={{
                  color: isLowEngagement ? '#d97706' : '#059669'
                }} data-id="element-3052" data-mp="9i6d4ixis081">
                      Signatures
                    </p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-3053" data-mp="174wsmpl23gox">
                      {document.signatureCount} of {document.totalWorkers} (
                      {engagementRate}%)
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-3 p-3 bg-white rounded-lg border border-blue-200" data-id="element-3054" data-mp="1l4o7esjed13z">
                <p className="text-xs text-gray-600 font-medium mb-1" data-id="element-3055" data-mp="1pv4p4ou938w5">
                  Activity
                </p>
                <p className="text-sm text-gray-900" data-id="element-3056" data-mp="3al0a1768csho">{document.activity}</p>
              </div>

              {isLowEngagement && <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2" data-id="element-3057" data-mp="35lnmek7jbhe1">
                  <Users size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-3058" data-mp="3jd28izwqnypb" />
                  <div data-id="element-3059" data-mp="2rme7dnv2etbn">
                    <p className="text-xs font-semibold text-amber-900" data-id="element-3060" data-mp="2rnpd3t82mb5e">
                      Low Engagement Alert
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5" data-id="element-3061" data-mp="2mpnvzdu7glwk">
                      Only {engagementRate}% of workers signed this PTP. Target
                      is 90%+.
                    </p>
                  </div>
                </div>}
            </div>

            {/* Document Images */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50" data-id="element-3062" data-mp="fyy2nzlpwyvt">
              <div className="space-y-4" data-id="element-3063" data-mp="axin2n2b7kkt">
                {document.imageUrls.map((url, index) => <div key={index} className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200" data-id="element-3064" data-mp="34pqm0n956o8u" data-mpi={index}>
                    <div className="px-4 py-2 bg-gray-100 border-b border-gray-200" data-id="element-3065" data-mp="3drzuz8ori9k4">
                      <p className="text-xs font-semibold text-gray-600" data-id="element-3066" data-mp="3iq1ajjmzbqqu">
                        Page {index + 1} of {document.imageUrls.length}
                      </p>
                    </div>
                    <img src={url} alt={`PTP Document Page ${index + 1}`} className="w-full h-auto" data-id="element-3067" data-mp="2q0cdfhde4aui" />
                  </div>)}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50" data-id="element-3068" data-mp="22cb80hbidajb">
              <button onClick={onClose} className="w-full py-2.5 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors" data-id="element-3069" data-mp="27ameqj6ib4xb">
                Close
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}